import { useEffect, useMemo, useState } from "react";

import {
  applySegmentationStrategyDiff,
  approveSegmentationStrategy,
  fetchLatestSegmentationStrategy,
  proposeSegmentationStrategy,
  runSegmentationV2,
  sendSegmentationStrategyMessage,
  type RoadmapGateReason,
  type RoadmapSegmentationStrategyPlan,
} from "../../api/roadmapClient";
import { InlineAlert } from "../InlineAlert";

export function SegmentationStrategyPanel({ projectId }: { projectId: string }): JSX.Element {
  const [plan, setPlan] = useState<RoadmapSegmentationStrategyPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [sending, setSending] = useState(false);
  const [applyingDiff, setApplyingDiff] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [assistantReply, setAssistantReply] = useState<string | null>(null);
  const [assistantRationale, setAssistantRationale] = useState<string | null>(null);
  const [proposedPatchJson, setProposedPatchJson] = useState<Record<string, unknown> | null>(null);
  const [messageReasons, setMessageReasons] = useState<RoadmapGateReason[]>([]);

  const reloadLatestPlan = async (): Promise<void> => {
    const next = await fetchLatestSegmentationStrategy(projectId);
    setPlan(next);
  };

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    reloadLatestPlan()
      .then(() => setError(null))
      .catch((err) => setError(err instanceof Error ? err.message : "Impossible de charger la stratégie."))
      .finally(() => setLoading(false));
  }, [projectId]);

  const canApprove = useMemo(() => Boolean(plan && plan.stopReason !== "FAIL"), [plan]);
  const canRun = useMemo(() => Boolean(plan?.status === "APPROVED"), [plan]);

  return (
    <div className="space-y-4">
      <div className="rounded border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="button button-primary button-sm"
            onClick={async () => {
              try {
                setLoading(true);
                const response = await proposeSegmentationStrategy(projectId);
                setPlan(response.plan);
                setStatus(`Plan proposé (${response.stopReason})`);
                setAssistantReply(null);
                setAssistantRationale(null);
                setProposedPatchJson(null);
              } catch (err) {
                setError(err instanceof Error ? err.message : "Proposition impossible.");
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
          >
            {loading ? "Proposition..." : "Proposer stratégie"}
          </button>
          <button
            type="button"
            className="button button-secondary button-sm"
            onClick={async () => {
              if (!plan || !message.trim()) return;
              try {
                setSending(true);
                const response = await sendSegmentationStrategyMessage(plan.id, message.trim());
                setPlan(response.plan ?? plan);
                setAssistantReply(response.assistantReply ?? null);
                setAssistantRationale(response.rationale ?? null);
                setProposedPatchJson(response.proposedPatchJson ?? null);
                setMessageReasons(response.reasons ?? []);
                setStatus(`Message traité (${response.stopReason}).`);
                setMessage("");
              } catch (err) {
                setError(err instanceof Error ? err.message : "Message impossible.");
              } finally {
                setSending(false);
              }
            }}
            disabled={!plan || sending || loading}
          >
            {sending ? "Envoi..." : "Envoyer"}
          </button>
          <button
            type="button"
            className="button button-secondary button-sm"
            onClick={async () => {
              if (!plan || !proposedPatchJson) return;
              try {
                setApplyingDiff(true);
                await applySegmentationStrategyDiff(plan.id, proposedPatchJson);
                await reloadLatestPlan();
                setStatus("Diff appliqué.");
              } catch (err) {
                setError(err instanceof Error ? err.message : "Diff impossible.");
              } finally {
                setApplyingDiff(false);
              }
            }}
            disabled={!plan || !proposedPatchJson || applyingDiff}
          >
            {applyingDiff ? "Application..." : "Appliquer le diff"}
          </button>
          <button
            type="button"
            className="button button-primary button-sm"
            onClick={async () => {
              if (!plan) return;
              try {
                const updated = await approveSegmentationStrategy(plan.id);
                setPlan(updated);
                setStatus("Gate approuvé ✅");
              } catch (err) {
                setError(err instanceof Error ? err.message : "Approbation impossible.");
              }
            }}
            disabled={!canApprove}
          >
            Approuver
          </button>
          <button
            type="button"
            className="button button-primary button-sm"
            onClick={async () => {
              try {
                setRunning(true);
                const result = await runSegmentationV2(projectId);
                setStatus(`Segmentation run: ${result.stopReason}`);
                await reloadLatestPlan();
              } catch (err) {
                setError(err instanceof Error ? err.message : "Run impossible.");
              } finally {
                setRunning(false);
              }
            }}
            disabled={!canRun || running}
          >
            {running ? "Run..." : "Run Segmentation"}
          </button>
        </div>

        <div className="mt-3 grid gap-3 md:grid-cols-6">
          <MetricCard label="Status" value={plan?.status ?? "-"} />
          <MetricCard label="Stop reason" value={plan?.stopReason ?? "-"} />
          <MetricCard label="Plan version" value={String(plan?.planVersion ?? "-")} />
          <MetricCard label="Ruleset" value={plan?.rulesetVersion ?? "-"} />
          <MetricCard label="LLM model" value={plan?.llmModel ?? "-"} />
          <MetricCard label="Prompt" value={plan?.llmPromptVersion ?? "-"} />
        </div>

        {plan ? (
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="rounded border border-slate-200 p-3 text-xs">
              <div className="font-semibold text-slate-700">Plan recommandé</div>
              <pre className="mt-2 overflow-auto whitespace-pre-wrap text-[11px] text-slate-600">{JSON.stringify(plan.paramsJson, null, 2)}</pre>
            </div>
            <div className="rounded border border-slate-200 p-3 text-xs">
              <div className="font-semibold text-slate-700">Examples + Exceptions</div>
              <pre className="mt-2 overflow-auto whitespace-pre-wrap text-[11px] text-slate-600">{JSON.stringify({ examples: plan.examplesJson, exceptions: plan.exceptionsJson }, null, 2)}</pre>
            </div>
          </div>
        ) : null}

        <label className="mt-3 flex flex-col gap-1 text-xs text-slate-500">
          Conversation
          <textarea className="input min-h-[110px]" value={message} onChange={(event) => setMessage(event.target.value)} />
        </label>

        {assistantReply ? (
          <div className="mt-3 rounded border border-slate-200 p-3 text-xs">
            <div className="font-semibold text-slate-700">Réponse assistant</div>
            <div className="mt-1 whitespace-pre-wrap text-slate-600">{assistantReply}</div>
            {assistantRationale ? <div className="mt-2 whitespace-pre-wrap text-slate-500">{assistantRationale}</div> : null}
            {messageReasons.length ? (
              <ul className="mt-2 list-disc pl-4 text-slate-500">
                {messageReasons.map((reason) => (
                  <li key={`${reason.code}-${reason.message}`}>{reason.code}: {reason.message}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {proposedPatchJson ? (
          <div className="mt-3 rounded border border-slate-200 p-3 text-xs">
            <div className="font-semibold text-slate-700">Diff preview (JSON patch)</div>
            <pre className="mt-2 overflow-auto whitespace-pre-wrap text-[11px] text-slate-600">{JSON.stringify(proposedPatchJson, null, 2)}</pre>
          </div>
        ) : null}
      </div>
      {status ? <InlineAlert tone="info" title="Segmentation Strategy" description={status} /> : null}
      {error ? <InlineAlert tone="danger" title="Segmentation Strategy" description={error} /> : null}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-700">{value}</div>
    </div>
  );
}
